import React, { useState, useRef } from 'react';
import leagueApiService from '../services/leagueApi';
import { CogIcon } from './icons/StreamlineFlexSolid';
import { resizeImageToDataUrl } from '../utils/logoImageUtils';

const LOGO_EMOJIS = ['⚾', '🏆', '🔥', '⚡', '🌟', '💪', '🎯', '👑', '🦅', '🐻', '🐯', '🦁'];
const LOGO_COLORS = [
  { name: 'Blue', value: 'var(--primary)' },
  { name: 'Red', value: 'var(--destructive)' },
  { name: 'Green', value: 'var(--success)' },
  { name: 'Orange', value: '#fd7e14' },
  { name: 'Purple', value: '#6f42c1' },
  { name: 'Teal', value: '#20c997' },
  { name: 'Navy', value: '#001f3f' },
  { name: 'Crimson', value: '#85144b' },
];

function TeamSettingsModal({ userTeam, league, setLeague, onClose, onSuccess, onError }) {
  const [saving, setSaving] = useState(false);
  const [teamName, setTeamName] = useState(userTeam?.name || '');
  const [abbreviation, setAbbreviation] = useState(userTeam?.acronym || '');
  const [logoEmoji, setLogoEmoji] = useState(userTeam?.logoEmoji || '⚾');
  const [logoColor, setLogoColor] = useState(userTeam?.logoColor || 'var(--primary)');
  const [logoImage, setLogoImage] = useState(userTeam?.logoImage || '');
  const [logoImageError, setLogoImageError] = useState('');
  const [logoImageLoading, setLogoImageLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [secondaryOwners, setSecondaryOwners] = useState(userTeam?.secondaryOwners || []);
  const [newOwnerEmail, setNewOwnerEmail] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      const teams = Array.isArray(league.teams) ? [...league.teams] :
        (typeof league.teams === 'string' ? JSON.parse(league.teams || '[]') : []);
      const teamIndex = teams.findIndex(t => (t.id || t.teamId) === (userTeam.id || userTeam.teamId));
      if (teamIndex === -1) throw new Error('Team not found');

      teams[teamIndex] = {
        ...teams[teamIndex],
        name: teamName.trim() || teams[teamIndex].name,
        acronym: abbreviation.trim().toUpperCase() || teams[teamIndex].acronym,
        logoEmoji,
        logoColor,
        logoImage: logoImage || undefined,
        secondaryOwners: secondaryOwners.filter(Boolean),
      };

      const result = await leagueApiService.updateLeague(league.id || league.leagueId, {
        teams: JSON.stringify(teams),
      });

      if (result.success && result.league) {
        setLeague({ ...league, teams: result.league.teams || teams });
      } else {
        setLeague({ ...league, teams });
      }
      onSuccess('Team settings saved');
      onClose();
    } catch (err) {
      onError(err.message || 'Failed to save team settings');
    } finally {
      setSaving(false);
    }
  };

  const addSecondaryOwner = () => {
    const email = newOwnerEmail.trim().toLowerCase();
    if (!email) return;
    if (secondaryOwners.includes(email)) {
      onError('Email already added');
      return;
    }
    setSecondaryOwners([...secondaryOwners, email]);
    setNewOwnerEmail('');
  };

  const removeSecondaryOwner = (email) => {
    setSecondaryOwners(secondaryOwners.filter(e => e !== email));
  };

  const handleLogoImageChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setLogoImageError('');
    if (!file.type.startsWith('image/')) {
      setLogoImageError('Please select an image file (JPEG, PNG, etc.).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoImageError('Image must be under 5 MB.');
      return;
    }
    setLogoImageLoading(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 200, 0.85);
      setLogoImage(dataUrl);
    } catch (err) {
      setLogoImageError(err.message || 'Failed to process image.');
    } finally {
      setLogoImageLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeLogoImage = () => {
    setLogoImage('');
    setLogoImageError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="card"
        style={{
          maxWidth: '440px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CogIcon size={20} />
            Team Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--muted-foreground)' }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="form-group">
          <label>Team Name</label>
          <input
            type="text"
            className="form-control"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="Enter team name"
          />
        </div>

        <div className="form-group">
          <label>Abbreviation (up to 4 characters)</label>
          <input
            type="text"
            className="form-control"
            value={abbreviation}
            onChange={(e) => setAbbreviation(e.target.value.slice(0, 4).toUpperCase())}
            placeholder="e.g., TEAM"
            maxLength={4}
            style={{ textTransform: 'uppercase' }}
          />
        </div>

        <div className="form-group">
          <label>Logo Emoji</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {LOGO_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => setLogoEmoji(emoji)}
                style={{
                  width: '40px',
                  height: '40px',
                  fontSize: '20px',
                  border: logoEmoji === emoji ? '2px solid var(--primary)' : '1px solid var(--border)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--card)',
                  cursor: 'pointer',
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Logo Color</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {LOGO_COLORS.map(({ value }) => (
              <button
                key={value}
                type="button"
                onClick={() => setLogoColor(value)}
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: value,
                  border: logoColor === value ? '3px solid #333' : '1px solid var(--border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
                title={value}
              />
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Team Logo Image</label>
          <p style={{ fontSize: '13px', color: 'var(--muted-foreground)', marginBottom: '8px' }}>
            Upload an image to use as your team logo (replaces emoji). Max 5 MB; will be resized.
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoImageChange}
              disabled={logoImageLoading}
              style={{ fontSize: '14px' }}
            />
            {logoImage && (
              <button type="button" className="btn btn-secondary" onClick={removeLogoImage} style={{ fontSize: '13px' }}>
                Remove image
              </button>
            )}
          </div>
          {logoImageLoading && <span style={{ fontSize: '13px', color: 'var(--primary)' }}>Processing…</span>}
          {logoImageError && <div style={{ fontSize: '13px', color: 'var(--destructive)', marginTop: '4px' }}>{logoImageError}</div>}
        </div>

        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--muted)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '10%',
              backgroundColor: logoColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              overflow: 'hidden',
            }}>
              {logoImage ? (
                <img src={logoImage} alt="Team logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transform: 'scale(0.8)' }}>
                  {logoEmoji}
                </span>
              )}
            </div>
            <div>
              <div style={{ fontWeight: '600', fontSize: '16px' }}>{teamName || 'Team Name'}</div>
              {abbreviation && <div style={{ fontSize: '13px', color: 'var(--muted-foreground)' }}>{abbreviation}</div>}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Secondary Owners</label>
          <p style={{ fontSize: '13px', color: 'var(--muted-foreground)', marginBottom: '8px' }}>
            Invite co-owners by email. They will be able to manage this team.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              type="email"
              className="form-control"
              value={newOwnerEmail}
              onChange={(e) => setNewOwnerEmail(e.target.value)}
              placeholder="email@example.com"
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSecondaryOwner())}
            />
            <button type="button" className="btn btn-secondary" onClick={addSecondaryOwner}>
              Add
            </button>
          </div>
          {secondaryOwners.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {secondaryOwners.map((email) => (
                <li
                  key={email}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: 'var(--muted)',
                    borderRadius: '6px',
                    marginBottom: '8px',
                  }}
                >
                  <span style={{ fontSize: '14px' }}>{email}</span>
                  <button
                    type="button"
                    onClick={() => removeSecondaryOwner(email)}
                    style={{ background: 'none', border: 'none', color: 'var(--destructive)', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamSettingsModal;
